import React from 'react';
import { TrendingUpIcon, UsersIcon, DollarSignIcon, StarIcon, CalendarIcon, ClockIcon } from 'lucide-react';
interface PerformanceDashboardProps {
  doctorId: number;
}
export function PerformanceDashboard({
  doctorId
}: PerformanceDashboardProps) {
  const performanceMetrics = {
    consultationsToday: 12,
    consultationsThisWeek: 58,
    consultationsThisMonth: 245,
    averageRating: 4.8,
    totalRatings: 1250,
    revenueToday: 1800,
    revenueThisWeek: 8700,
    revenueThisMonth: 36750,
    averageConsultationTime: 25,
    patientSatisfaction: 96
  };
  const weeklyData = [{
    day: 'Mon',
    consultations: 8,
    revenue: 1200
  }, {
    day: 'Tue',
    consultations: 12,
    revenue: 1800
  }, {
    day: 'Wed',
    consultations: 10,
    revenue: 1500
  }, {
    day: 'Thu',
    consultations: 14,
    revenue: 2100
  }, {
    day: 'Fri',
    consultations: 9,
    revenue: 1350
  }, {
    day: 'Sat',
    consultations: 5,
    revenue: 750
  }, {
    day: 'Sun',
    consultations: 0,
    revenue: 0
  }];
  const recentReviews = [{
    id: 1,
    patient: 'John Doe',
    rating: 5,
    comment: 'Excellent doctor, very thorough and caring.',
    date: '2024-01-15'
  }, {
    id: 2,
    patient: 'Jane Smith',
    rating: 5,
    comment: 'Great experience, highly recommended!',
    date: '2024-01-14'
  }, {
    id: 3,
    patient: 'Bob Wilson',
    rating: 4,
    comment: 'Good consultation, wait time was a bit long.',
    date: '2024-01-13'
  }];
  return <div className="space-y-6" data-id="element-521">
      <h2 className="text-xl font-bold text-gray-900" data-id="element-522">Performance Dashboard</h2>
      {/* Key Metrics */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4" data-id="element-523">
        <div className="bg-gradient-to-br from-blue-500 to-blue-600 rounded-lg p-6 text-white" data-id="element-524">
          <div className="flex items-center justify-between mb-2" data-id="element-525">
            <CalendarIcon className="w-6 h-6 opacity-80" data-id="element-526" />
            <span className="text-sm opacity-80" data-id="element-527">Today</span>
          </div>
          <div className="text-3xl font-bold mb-1" data-id="element-528">
            {performanceMetrics.consultationsToday}
          </div>
          <div className="text-sm opacity-90" data-id="element-529">Consultations</div>
        </div>
        <div className="bg-gradient-to-br from-green-500 to-green-600 rounded-lg p-6 text-white" data-id="element-530">
          <div className="flex items-center justify-between mb-2" data-id="element-531">
            <DollarSignIcon className="w-6 h-6 opacity-80" data-id="element-532" />
            <span className="text-sm opacity-80" data-id="element-533">Today</span>
          </div>
          <div className="text-3xl font-bold mb-1" data-id="element-534">
            ${performanceMetrics.revenueToday}
          </div>
          <div className="text-sm opacity-90" data-id="element-535">Revenue</div>
        </div>
        <div className="bg-gradient-to-br from-yellow-500 to-yellow-600 rounded-lg p-6 text-white" data-id="element-536">
          <div className="flex items-center justify-between mb-2" data-id="element-537">
            <StarIcon className="w-6 h-6 opacity-80" data-id="element-538" />
            <span className="text-sm opacity-80" data-id="element-539">Overall</span>
          </div>
          <div className="text-3xl font-bold mb-1" data-id="element-540">
            {performanceMetrics.averageRating}
          </div>
          <div className="text-sm opacity-90" data-id="element-541">Average Rating</div>
        </div>
        <div className="bg-gradient-to-br from-purple-500 to-purple-600 rounded-lg p-6 text-white" data-id="element-542">
          <div className="flex items-center justify-between mb-2" data-id="element-543">
            <ClockIcon className="w-6 h-6 opacity-80" data-id="element-544" />
            <span className="text-sm opacity-80" data-id="element-545">Average</span>
          </div>
          <div className="text-3xl font-bold mb-1" data-id="element-546">
            {performanceMetrics.averageConsultationTime}
          </div>
          <div className="text-sm opacity-90" data-id="element-547">Minutes/Consultation</div>
        </div>
      </div>
      {/* Monthly Summary */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4" data-id="element-548">
        <div className="bg-white border border-gray-200 rounded-lg p-6" data-id="element-549">
          <div className="flex items-center space-x-3 mb-4" data-id="element-550">
            <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center" data-id="element-551">
              <CalendarIcon className="w-6 h-6 text-blue-600" data-id="element-552" />
            </div>
            <div data-id="element-553">
              <p className="text-sm text-gray-600" data-id="element-554">This Month</p>
              <p className="text-2xl font-bold text-gray-900" data-id="element-555">
                {performanceMetrics.consultationsThisMonth}
              </p>
            </div>
          </div>
          <p className="text-sm text-gray-600" data-id="element-556">Total Consultations</p>
        </div>
        <div className="bg-white border border-gray-200 rounded-lg p-6" data-id="element-557">
          <div className="flex items-center space-x-3 mb-4" data-id="element-558">
            <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center" data-id="element-559">
              <DollarSignIcon className="w-6 h-6 text-green-600" data-id="element-560" />
            </div>
            <div data-id="element-561">
              <p className="text-sm text-gray-600" data-id="element-562">This Month</p>
              <p className="text-2xl font-bold text-gray-900" data-id="element-563">
                ${performanceMetrics.revenueThisMonth}
              </p>
            </div>
          </div>
          <p className="text-sm text-gray-600" data-id="element-564">Total Revenue</p>
        </div>
        <div className="bg-white border border-gray-200 rounded-lg p-6" data-id="element-565">
          <div className="flex items-center space-x-3 mb-4" data-id="element-566">
            <div className="w-12 h-12 bg-purple-100 rounded-lg flex items-center justify-center" data-id="element-567">
              <UsersIcon className="w-6 h-6 text-purple-600" data-id="element-568" />
            </div>
            <div data-id="element-569">
              <p className="text-sm text-gray-600" data-id="element-570">Satisfaction</p>
              <p className="text-2xl font-bold text-gray-900" data-id="element-571">
                {performanceMetrics.patientSatisfaction}%
              </p>
            </div>
          </div>
          <p className="text-sm text-gray-600" data-id="element-572">Patient Satisfaction</p>
        </div>
      </div>
      {/* Weekly Performance Chart */}
      <div className="bg-white border border-gray-200 rounded-lg p-6" data-id="element-573">
        <h3 className="text-lg font-semibold text-gray-900 mb-4" data-id="element-574">
          Weekly Performance
        </h3>
        <div className="space-y-4" data-id="element-575">
          {weeklyData.map(day => <div key={day.day} className="space-y-2" data-id="element-576">
              <div className="flex items-center justify-between text-sm" data-id="element-577">
                <span className="font-medium text-gray-900 w-12" data-id="element-578">
                  {day.day}
                </span>
                <span className="text-gray-600" data-id="element-579">
                  {day.consultations} consultations
                </span>
                <span className="text-gray-900 font-medium" data-id="element-580">
                  ${day.revenue}
                </span>
              </div>
              <div className="w-full bg-gray-200 rounded-full h-2" data-id="element-581">
                <div className="bg-gradient-to-r from-purple-500 to-blue-500 h-2 rounded-full" style={{
              width: `${day.consultations / 14 * 100}%`
            }} data-id="element-582" />
              </div>
            </div>)}
        </div>
      </div>
      {/* Patient Ratings & Reviews */}
      <div className="bg-white border border-gray-200 rounded-lg p-6" data-id="element-583">
        <div className="flex items-center justify-between mb-4" data-id="element-584">
          <h3 className="text-lg font-semibold text-gray-900" data-id="element-585">
            Recent Patient Reviews
          </h3>
          <div className="flex items-center space-x-2" data-id="element-586">
            <StarIcon className="w-5 h-5 text-yellow-500 fill-current" data-id="element-587" />
            <span className="font-semibold text-gray-900" data-id="element-588">
              {performanceMetrics.averageRating}
            </span>
            <span className="text-sm text-gray-500" data-id="element-589">
              ({performanceMetrics.totalRatings} ratings)
            </span>
          </div>
        </div>
        <div className="space-y-4" data-id="element-590">
          {recentReviews.map(review => <div key={review.id} className="p-4 border border-gray-200 rounded-lg" data-id="element-591">
              <div className="flex items-start justify-between mb-2" data-id="element-592">
                <div data-id="element-593">
                  <h4 className="font-medium text-gray-900" data-id="element-594">
                    {review.patient}
                  </h4>
                  <p className="text-xs text-gray-500" data-id="element-595">{review.date}</p>
                </div>
                <div className="flex items-center space-x-1" data-id="element-596">
                  {[...Array(5)].map((_, i) => <StarIcon key={i} className={`w-4 h-4 ${i < review.rating ? 'text-yellow-500 fill-current' : 'text-gray-300'}`} data-id="element-597" />)}
                </div>
              </div>
              <p className="text-sm text-gray-700" data-id="element-598">{review.comment}</p>
            </div>)}
        </div>
      </div>
    </div>;
}